import { ApiError } from "../api";
import type { Dict } from "./i18n";

/**
 * Turning a refused call into a sentence the operator can read.
 *
 * The server answers `{ error, code }` (chat-server `lib/errors.ts`). The
 * sentence is Turkish, always — it is written for logs and for anything that
 * is not this console. The console has both languages, so it looks the `code`
 * up in its own dictionary and only falls back to the server's sentence for a
 * code it has never heard of: that way a console running against a newer
 * server still says something true rather than nothing.
 */

/** The `code` an ApiError carries, or null — anything that is not a string is not a code. */
export function errorCodeOf(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const code = err.body["code"];
  return typeof code === "string" && code ? code : null;
}

/** The seconds a lockout asks for; 0 when the server did not say. */
function retryAfterSeconds(err: unknown): number {
  if (!(err instanceof ApiError)) return 0;
  const seconds = err.body["retryAfterSeconds"];
  return typeof seconds === "number" && Number.isFinite(seconds) ? Math.max(0, Math.round(seconds)) : 0;
}

export function apiErrorText(err: unknown, t: Dict, fallback: string): string {
  const code = errorCodeOf(err);
  if (code) {
    const entry = (t.errors as Record<string, string | ((n: number) => string)>)[code];
    if (typeof entry === "string") return entry;
    if (typeof entry === "function") return entry(retryAfterSeconds(err));
  }
  if (err instanceof Error && err.message.trim()) return err.message;
  return fallback;
}
